import { CartLinesUpdateEvent } from '@shopify/events';

class LouisMonacoPairsWellWith extends HTMLElement {
  #products = [];

  connectedCallback() {
    this.#load();
  }

  async #load() {
    try {
      const [complementary, related] = await Promise.all([
        this.#fetchRecommendations('complementary'),
        this.#fetchRecommendations('related'),
      ]);

      const unique = new Map();
      [...complementary, ...related].forEach((product) => {
        if (!product || String(product.id) === String(this.dataset.productId)) return;
        if (product.requires_selling_plan) return;
        if (!product.variants?.some((variant) => variant.available && !variant.requires_selling_plan)) return;
        if (!unique.has(String(product.id))) unique.set(String(product.id), product);
      });

      this.#products = [...unique.values()].slice(0, 3);
      if (!this.#products.length) {
        this.hidden = true;
        return;
      }

      this.#render();
    } catch (error) {
      console.warn('[Louis Monaco] Recommandations compactes indisponibles', error);
      this.hidden = true;
    }
  }

  async #fetchRecommendations(intent) {
    const base = this.dataset.recommendationsUrl || '/recommendations/products';
    const url = `${base}.json?product_id=${encodeURIComponent(this.dataset.productId)}&limit=3&intent=${intent}`;
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data.products) ? data.products : [];
  }

  #availableVariants(product) {
    return (product.variants || []).filter(
      (variant) => variant.available && !variant.requires_selling_plan
    );
  }

  #initialSelection(product) {
    return (product.options || []).map((option) => {
      const values = Array.isArray(option.values) ? option.values : [];
      return values.length === 1 ? String(values[0]) : '';
    });
  }

  #variantMatches(variant, selected, ignoredIndex = -1) {
    return selected.every((value, index) => {
      if (index === ignoredIndex || !value) return true;
      return String(variant.options?.[index] ?? '') === String(value);
    });
  }

  #findExactVariant(product, selected) {
    if (!selected.length || selected.some((value) => !value)) return null;
    return (
      this.#availableVariants(product).find((variant) =>
        selected.every((value, index) => String(variant.options?.[index] ?? '') === String(value))
      ) || null
    );
  }

  #findDisplayVariant(product, selected) {
    const available = this.#availableVariants(product);
    if (!available.length) return null;
    return available.find((variant) => this.#variantMatches(variant, selected)) || available[0];
  }

  #optionValueAvailable(product, selected, optionIndex, value) {
    const probe = [...selected];
    probe[optionIndex] = String(value);
    return this.#availableVariants(product).some((variant) => this.#variantMatches(variant, probe));
  }

  #reconcileSelection(product, selected, changedIndex) {
    const next = [...selected];
    let changed = true;

    while (changed) {
      changed = false;
      next.forEach((value, index) => {
        if (!value || index === changedIndex) return;
        if (!this.#optionValueAvailable(product, next, index, value)) {
          next[index] = '';
          changed = true;
        }
      });
    }

    return next;
  }

  #money(cents) {
    const currency = window.Shopify?.currency?.active || 'EUR';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format((Number(cents) || 0) / 100);
  }

  #imageUrl(product, variant) {
    const variantImage = variant?.featured_image?.src || variant?.featured_image;
    const productImage = product.featured_image?.src || product.featured_image || product.images?.[0];
    const src = variantImage || productImage;
    if (!src) return '';
    return String(src).startsWith('//') ? `https:${src}` : String(src);
  }

  #placeholder(option) {
    const name = String(option?.name || 'Option').trim();
    return `Choisir ${name.toLowerCase()}`;
  }

  #renderPrices(container, variant) {
    container.replaceChildren();
    if (!variant) return;

    const price = Number(variant.price) || 0;
    const compareAt = Number(variant.compare_at_price) || 0;

    if (compareAt > price) {
      const compare = document.createElement('span');
      compare.className = 'lm-pairs__compare';
      compare.textContent = this.#money(compareAt);
      container.append(compare);
    }

    const current = document.createElement('span');
    current.className = 'lm-pairs__price';
    current.textContent = this.#money(price);
    container.append(current);

    if (compareAt > price) {
      const saving = document.createElement('span');
      saving.className = 'lm-pairs__saving';
      saving.textContent = `ÉCONOMISEZ ${this.#money(compareAt - price)}`;
      container.append(saving);
    }
  }

  #render() {
    const list = this.querySelector('[data-lm-pairs-list]');
    if (!list) return;
    list.replaceChildren();

    this.#products.forEach((product) => {
      const available = this.#availableVariants(product);
      if (!available.length) return;

      const state = {
        selected: this.#initialSelection(product),
        variant: null,
      };

      const item = document.createElement('article');
      item.className = 'lm-pairs__item';
      item.dataset.productId = product.id;

      const media = document.createElement('a');
      media.className = 'lm-pairs__media';
      media.href = product.url;
      media.setAttribute('aria-label', product.title);

      const image = document.createElement('img');
      image.className = 'lm-pairs__image';
      image.alt = product.title;
      image.loading = 'lazy';
      image.width = 176;
      image.height = 220;
      media.append(image);

      const content = document.createElement('div');
      content.className = 'lm-pairs__content';

      const title = document.createElement('a');
      title.className = 'lm-pairs__title';
      title.href = product.url;
      title.textContent = product.title;

      const prices = document.createElement('div');
      prices.className = 'lm-pairs__prices';

      const options = document.createElement('div');
      options.className = 'lm-pairs__options';

      const selects = [];
      (product.options || []).forEach((option, optionIndex) => {
        const values = Array.isArray(option.values) ? option.values : [];
        if (values.length <= 1) return;

        const control = document.createElement('div');
        control.className = 'lm-pairs__option';

        const label = document.createElement('label');
        label.className = 'lm-pairs__option-label';
        const selectId = `lm-pairs-${product.id}-${optionIndex}`;
        label.htmlFor = selectId;
        label.textContent = option.name;

        const select = document.createElement('select');
        select.className = 'lm-pairs__select';
        select.id = selectId;
        select.dataset.optionIndex = String(optionIndex);
        select.setAttribute('aria-label', option.name);

        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = this.#placeholder(option);
        select.append(placeholder);

        values.forEach((value) => {
          const optionElement = document.createElement('option');
          optionElement.value = String(value);
          optionElement.textContent = String(value);
          select.append(optionElement);
        });

        select.addEventListener('change', () => {
          state.selected[optionIndex] = select.value;
          state.selected = this.#reconcileSelection(product, state.selected, optionIndex);
          update();
        });

        selects.push(select);
        control.append(label, select);
        options.append(control);
      });

      const button = document.createElement('button');
      button.className = 'lm-pairs__add';
      button.type = 'button';
      button.addEventListener('click', () => {
        if (state.variant) this.#add(product, state.variant, button);
      });

      const update = () => {
        selects.forEach((select) => {
          const optionIndex = Number(select.dataset.optionIndex);
          Array.from(select.options).forEach((optionElement, index) => {
            if (index === 0) return;
            optionElement.disabled = !this.#optionValueAvailable(
              product,
              state.selected,
              optionIndex,
              optionElement.value
            );
          });
          select.value = state.selected[optionIndex] || '';
        });

        state.variant = this.#findExactVariant(product, state.selected);
        const displayVariant = state.variant || this.#findDisplayVariant(product, state.selected);
        const imageUrl = this.#imageUrl(product, displayVariant);

        if (imageUrl) {
          image.src = imageUrl;
          image.hidden = false;
        } else {
          image.removeAttribute('src');
          image.hidden = true;
        }

        this.#renderPrices(prices, displayVariant);

        const requiresSelection = (product.options || []).some((option, index) => {
          const values = Array.isArray(option.values) ? option.values : [];
          return values.length > 1 && !state.selected[index];
        });

        button.disabled = !state.variant;
        button.textContent = state.variant
          ? '+ AJOUTER'
          : requiresSelection
            ? 'CHOISIR LES OPTIONS'
            : 'INDISPONIBLE';
      };

      content.append(title, prices);
      if (options.children.length) content.append(options);
      content.append(button);
      item.append(media, content);
      list.append(item);
      update();
    });

    if (!list.children.length) this.hidden = true;
  }

  async #add(product, variant, button) {
    if (!variant || button.disabled) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.dataset.loading = 'true';
    button.textContent = 'AJOUT...';

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
      });
      const added = await response.json();
      if (!response.ok || added.status) {
        throw new Error(added.description || added.message || 'Ajout au panier impossible');
      }

      const cartResponse = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'lm-pairs-well-with',
          sourceId: String(product.id),
          itemCount: 1,
          productId: String(product.id),
        },
      });

      button.textContent = 'AJOUTÉ';
      setTimeout(() => {
        delete button.dataset.loading;
        button.disabled = false;
        button.textContent = originalText;
      }, 1200);
    } catch (error) {
      deferred.reject(error);
      console.warn('[Louis Monaco] Ajout recommandation impossible', error);
      delete button.dataset.loading;
      button.disabled = false;
      button.textContent = originalText;
    }
  }
}

if (!customElements.get('lm-pairs-well-with')) {
  customElements.define('lm-pairs-well-with', LouisMonacoPairsWellWith);
}
